import { useEffect, useMemo, useRef, useState } from 'react';
import { getArbitrageResults } from '../../utils/api/getArbitrageResults';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Checkbox, Button, Paper, Box, Typography, TextField, Tab, Tooltip, Popover } from '@mui/material';
import wwlogo_gif from '../../assets/ww_loading2.gif';
import { addPlusSign, removeObjectWithProperty } from '../evscreen/EvScreenTable/evScreenTable';
import { LogoGenerator } from '../../components/LogoGenerator';
import StakeInput from './StakeInput';
import { BetLinkButton } from '../../components/BetLinkButton';
import { getGamesData, getLinksData } from '../../utils/api/evScreen';
import play_icon from '../../assets/images/play_icon.svg';
import './Arbitrage.css';
import axios from 'axios';
import { removeBetPlaced, setBetPlaced } from '../../utils/api/setBetPlaced';
import lightbulb_icon from '../../assets/images/lightbulb_icon.svg';
import filter_icon from '../../assets/images/filter_icon.svg';
import { getFutureDate } from '../../utils/getFutureDate';
import { FiltersContainerArbitrage } from './FiltersContainerArbitrage';
import { MobileArbitrageFilters } from './MobileArbitrageFilters';
import { getRelatedOdds } from '../../utils/api/getRelatedOdds';
import { RelatedOddsDropdown } from '../evscreen/EvScreenTable/RelatedOddsDropdown';
import { IOSSwitch } from '../../components/IOSSwitch';
import { EVPercent } from '../../components/EVPercent';
import { UpgradeModal } from '../../components/UpgradeModal/UpgradeModal';
import { useMediaQuery, useTheme, Card, CardContent } from '@mui/material';
import { DesktopArbitrageButtons } from './DesktopArbitrageButtons';
import { MobileArbitrageButtons } from './MobileArbitrageButtons';

function calculateArbReturn(oddsA, stakeA, oddsB, stakeB) {
	const aOdds = parseFloat(oddsA);
	const aStake = parseFloat(stakeA);
	const bOdds = parseFloat(oddsB);
	const bStake = parseFloat(stakeB);

	// Graceful fallback if inputs are invalid
	if (!Number.isFinite(aOdds) || !Number.isFinite(aStake) || !Number.isFinite(bOdds) || !Number.isFinite(bStake)) {
		return {
			sideA_return: 0,
			sideB_return: 0,
			min_return: 0,
			max_return: 0,
		};
	}

	const returnA = aOdds > 0 ? aStake * (aOdds / 100) - bStake : aStake * (100 / Math.abs(aOdds)) - bStake;

	const returnB = bOdds > 0 ? bStake * (bOdds / 100) - aStake : bStake * (100 / Math.abs(bOdds)) - aStake;

	return {
		sideA_return: returnA,
		sideB_return: returnB,
		min_return: Math.min(returnA, returnB),
		max_return: Math.max(returnA, returnB),
	};
}

function toDecimal(odds) {
	return odds >= 0 ? 1 + odds / 100 : 1 - 100 / odds;
}
function calculateHedgeStake(oddsA, stakeA, oddsB) {
	const decA = toDecimal(oddsA);
	const decB = toDecimal(oddsB);
	return (stakeA * decA) / decB;
}

export const Arbitrage = (props) => {
	const { email, accessToken, bankroll, isMobile } = props;

	const [arbResults, setArbResults] = useState();
	const [isLoading, setIsLoading] = useState(true);
	const [isScannable, setIsScannable] = useState(true);
	const [refreshCounter, setRefreshCounter] = useState(0);
	const [isLive, setIsLive] = useState(false);
	const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
	const [selectedRow, setSelectedRow] = useState();
	const [selectedSport, setSelectedSport] = useState();
	const [selectedRowIndex, setSelectedRowIndex] = useState(null);
	const selectedRowRef = useRef();

	// Keep ref in sync with state
	useEffect(() => {
		selectedRowRef.current = selectedRow;
	}, [selectedRow]);

	const [placedBets, setPlacedBets] = useState([]);
	const user = JSON.parse(window.localStorage.getItem('userdb'));
	const [sortColumn, setSortColumn] = useState('roi');
	const [sortDirection, setSortDirection] = useState('desc');
	const [forceOptimalStakes, setForceOptimalStakes] = useState(true);
	const [globalStake, setGlobalStake] = useState(user?.query_arbitrage?.[0]?.[0]?.default_stake || 100);
	const [allBetsOpen, setAllBetsOpen] = useState('all');
	const [isPlacedUnplacedOpen, setIsPlacedUnplacedOpen] = useState(false);
	const [isPlacedAnchorEl, setIsPlacedAnchorEl] = useState();
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);
	const [relatedOdds, setRelatedOdds] = useState();
	const [isAutoRefresh, setIsAutoRefresh] = useState(false);
	const [queries, setQueries] = useState(user?.query_arbitrage || []);
	const [userState, setUserState] = useState(user);

	const userdb = JSON.parse(window.localStorage.getItem('userdb'));

	const isFree = userdb?.stripe_subscription === null || !userdb?.stripe_subscription || userdb?.stripe_status === 'deleted' || userdb?.stripe_status === 'past_due';
	const isApprentice = userdb?.stripe_plan?.nickname?.includes('Apprentice') && userdb?.stripe_status !== 'deleted' && userdb.stripe_status !== 'past_due';

	useEffect(() => {
		if (isFree || isApprentice) {
			setIsUpgradeModalOpen(true);
		}
	}, []);

	const date = getFutureDate(user?.queries?.[0]?.dateDifference || 7);

	const [filters, setFilters] = useState(
		user?.query_arbitrage?.find((q) => q?.[0]?.name === user.selected_query_arbitrage) ||
			(user?.query_arbitrage?.[0]
				? user?.query_arbitrage?.[0]
				: [
						{
							sports: user?.query_arbitrage?.[0].sports ? user?.query_arbitrage?.[0].sports : [],
							leagues: user?.query_arbitrage?.[0].leagues ? user?.query_arbitrage?.[0].leagues : [],
							sportsbooks: user?.query_arbitrage?.[0].sportsbooks ? user?.query_arbitrage?.[0].sportsbooks : [],
							sharpBooks: user?.query_arbitrage?.[0].sharpBooks
								? user?.query_arbitrage?.[0].sharpBooks
								: [
										// { label: 'WagerWiz Mains', value: 'WagerWiz Mains' },
										// { label: 'WagerWiz Props', value: 'WagerWiz Props' },
								  ],
							markets: user?.query_arbitrage?.[0].markets ? user?.query_arbitrage?.[0].markets : [],
							marketTypes: user?.query_arbitrage?.[0].marketTypes ? user?.query_arbitrage?.[0].marketTypes : [],
							minMaxEv: user?.query_arbitrage?.[0].minMaxEv ? user?.query_arbitrage?.[0].minMaxEv : [null, null],
							minMaxOdds: user?.query_arbitrage?.[0].minMaxOdds ? user?.query_arbitrage?.[0].minMaxOdds : [null, null],
							date: date ? date : new Date(),
							include_only_selected_books: user?.query_arbitrage?.[0].include_only_selected_books ? user?.query_arbitrage?.[0].include_only_selected_books : false,
							hide_alternates: user?.query_arbitrage?.[0].hide_alternates ? user?.query_arbitrage?.[0].hide_alternates : false,
						},
				  ])
	);

	useEffect(() => {
		const getArbResults = async () => {
			try {
				const res = await getArbitrageResults(email, filters, isLive, accessToken);
				const gameIds = [];
				const outcomeIdsOver = [];
				const outcomeIdsUnder = [];
				const sports = [];

				for (const row of res) {
					row.stakeA = Math.round(globalStake);
					row.stakeB = Math.round(calculateHedgeStake(row.bet_price_over, globalStake, row.bet_price_under));
					row.stakeAUserEdited = false;
					row.stakeBUserEdited = false;
					const game_id = row.outcome_id_normalized_both_books.split(':')[0];
					if (!gameIds.some((gameId) => gameId === game_id)) {
						gameIds.push(game_id);
					}
					if (!sports.includes(row.sport)) {
						sports.push(row.sport);
					}
					outcomeIdsOver.push(row.over_outcome_id_normalized);
					outcomeIdsUnder.push(row.under_outcome_id_normalized);
				}
				const gamesData = await getGamesData(gameIds, accessToken);
				const linksDataOver = await getLinksData(outcomeIdsOver, sports);
				const linksDataUnder = await getLinksData(outcomeIdsUnder, sports);

				for (const row of res) {
					const game = gamesData.games.find((g) => g.game_id === row.outcome_id_normalized_both_books.split(':')[0]);
					// getLinksData returns an object keyed by outcome_id_normalized, not an array
					const linkOver = linksDataOver[row.over_outcome_id_normalized] || null;
					const linkUnder = linksDataUnder[row.under_outcome_id_normalized] || null;

					row.game = game;
					let link_over;
					let link_under;
					if (linkOver?.deep_link_over?.desktop?.includes('betmgm')) {
						link_over = linkOver?.deep_link_over?.desktop?.replace(/<STATE>./g, 'on.').replace(/.com/g, '.ca');
					} else {
						link_over = linkOver?.deep_link_over?.desktop?.replace(/<STATE>./g, '');
					}

					if (linkUnder?.deep_link_over?.desktop?.includes('betmgm')) {
						link_under = linkUnder?.deep_link_under?.desktop?.replace(/<STATE>./g, 'on.').replace(/.com/g, '.ca');
					} else {
						link_under = linkUnder?.deep_link_under?.desktop?.replace(/<STATE>./g, '');
					}
					row.linkOver = link_over;
					row.linkUnder = link_under;
				}
				const sortedResults = res.sort((a, b) => b.roi - a.roi);
				setArbResults(sortedResults);
				
				// Preserve selected row if it still exists in new data
				// Use ref to get the latest value and avoid stale closures
				const currentSelectedRow = selectedRowRef.current;
				if (currentSelectedRow) {
					const selectedRowData = sortedResults.find((row) => row.outcome_id_normalized_both_books === currentSelectedRow);
					if (selectedRowData) {
						// Update the index to match new position
						const newIndex = sortedResults.findIndex((row) => row.outcome_id_normalized_both_books === currentSelectedRow);
						setSelectedRowIndex(newIndex);
						// Re-fetch related odds to ensure dropdown stays open with fresh data
						const relatedOddsData = await getRelatedOdds(
							selectedRowData.outcome_id_normalized_no_book,
							selectedRowData.sport,
							{ market: selectedRowData.market, points: selectedRowData.points, home_team: selectedRowData.game?.home_team_display || selectedRowData.home_team }
						);
						setRelatedOdds(relatedOddsData);
					} else {
						// Selected row no longer exists in new data, clear selection
						setSelectedRow();
						setSelectedSport();
						setSelectedRowIndex(null);
						setRelatedOdds();
					}
				}
				
				setIsLoading(false);
			} catch (e) {
				console.log('e: ', e);
				setIsLoading(false);
			}
		};
		if (!isFree && !isApprentice) {
			setIsLoading(true);
			getArbResults();

			if (isAutoRefresh) {
				let intervalId;

				if (isAutoRefresh) {
					intervalId = setInterval(async () => {
						// console.log('Autorefresh', new Date());
						await getArbResults();
					}, 10000);
				}

				return () => {
					if (intervalId) {
						clearInterval(intervalId);
					}
				};
			}
		} else {
			setIsLoading(false);
			setIsUpgradeModalOpen(true);
		}
	}, [refreshCounter, isLive, isAutoRefresh]);

	useEffect(() => {
		if (!arbResults) return;

		const updatedResults = arbResults.map((row) => ({
			...row,
			stakeA: row.stakeAUserEdited ? Math.round(row.stakeA) : Math.round(globalStake),
			stakeB: row.stakeBUserEdited ? Math.round(row.stakeB) : Math.round(calculateHedgeStake(row.bet_price_over, globalStake, row.bet_price_under)),
		}));

		setArbResults(updatedResults);
	}, [globalStake]);

	useEffect(() => {
		const getPlacedBets = async () => {
			try {
				const bets = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/get_placed_bets?email=${user.email}&arbitrage=true`, {
					headers: {
						Authorization: `Bearer ${accessToken}`,
					},
				});
				// console.log('bets: ', bets.data);
				// Parse value field if it's a JSON string for arbitrage bets
				const placedBetsArray = bets.data?.placed_bets || [];
				const parsedBets = placedBetsArray.map((bet) => {
					if (bet.value) {
						try {
							const parsed = JSON.parse(bet.value);
							if (parsed.bet1 !== undefined && parsed.bet2 !== undefined) {
								return { ...bet, bet1Value: parsed.bet1, bet2Value: parsed.bet2 };
							}
						} catch (e) {
							// Not JSON, keep as is
						}
					}
					return bet;
				});
				setPlacedBets(parsedBets);
			} catch (error) {
				console.error('Error fetching placed bets:', error);
				setPlacedBets([]);
			}
		};
		getPlacedBets();
	}, []);

	const visibleRows = useMemo(() => {
		let rows = [];
		rows = arbResults?.sort((a, b) => {
			let valA, valB;

			if (sortColumn === 'roi') {
				valA = a.roi ?? 0;
				valB = b.roi ?? 0;
			} else if (sortColumn === 'time') {
				valA = new Date(a.game?.start_date).getTime();
				valB = new Date(b.game?.start_date).getTime();
			}

			return sortDirection === 'asc' ? valA - valB : valB - valA;
		});

		if (allBetsOpen === 'placed') {
			rows = rows.filter((row) => placedBets.some((placedbet) => placedbet.id === row.outcome_id_normalized_both_books));
		} else if (allBetsOpen === 'unplaced') {
			rows = rows.filter((row) => !placedBets.some((placedbet) => placedbet.id === row.over_outcome_id_normalized_both_books));
		}

		return rows;
	}, [arbResults, allBetsOpen, placedBets, selectedRow, sortDirection, sortColumn]);

	return (
		<div style={{ backgroundColor: '#141416', height: '100%', position: 'relative', zIndex: 1 }}>
			<div className="px-4 md:px-4 xl:px-6 pt-6 mb-6">
				<div className="w-full max-w-[95vw] mx-auto">
					<h2 style={{ textAlign: isMobile ? 'center' : 'left' }} className="text-3xl ev-scanner-title">
						Arbitrage Bets
					</h2>
				</div>
			</div>

			{!isMobile && (
				<div className="px-4 md:px-4 xl:px-6">
					<DesktopArbitrageButtons
						setRefreshCounter={setRefreshCounter}
						setIsFiltersOpen={setIsFiltersOpen}
						setIsLoading={setIsLoading}
						refreshCounter={refreshCounter}
						isScannable={isScannable}
						setIsAutoRefresh={setIsAutoRefresh}
						isAutoRefresh={isAutoRefresh}
						setIsLive={setIsLive}
						isLive={isLive}
						setIsPlacedAnchorEl={setIsPlacedAnchorEl}
						setIsPlacedUnplacedOpen={setIsPlacedUnplacedOpen}
						isPlacedUnplacedOpen={isPlacedUnplacedOpen}
						setAllBetsOpen={setAllBetsOpen}
						allBetsOpen={allBetsOpen}
						isPlacedAnchorEl={isPlacedAnchorEl}
						globalStake={globalStake}
						setGlobalStake={setGlobalStake}
						isFiltersOpen={isFiltersOpen}
						forceOptimalStakes={forceOptimalStakes}
						setForceOptimalStakes={setForceOptimalStakes}
					/>
				</div>
			)}

			{isMobile && (
				<MobileArbitrageButtons
					setRefreshCounter={setRefreshCounter}
					setIsFiltersOpen={setIsFiltersOpen}
					setIsLoading={setIsLoading}
					refreshCounter={refreshCounter}
					isScannable={isScannable}
					setIsAutoRefresh={setIsAutoRefresh}
					isAutoRefresh={isAutoRefresh}
					setIsLive={setIsLive}
					isLive={isLive}
					setIsPlacedAnchorEl={setIsPlacedAnchorEl}
					setIsPlacedUnplacedOpen={setIsPlacedUnplacedOpen}
					isPlacedUnplacedOpen={isPlacedUnplacedOpen}
					setAllBetsOpen={setAllBetsOpen}
					allBetsOpen={allBetsOpen}
					isPlacedAnchorEl={isPlacedAnchorEl}
					globalStake={globalStake}
					setGlobalStake={setGlobalStake}
					isFiltersOpen={isFiltersOpen}
				/>
			)}

			{isFiltersOpen && (
				<div className="px-4 mb-6">
					<div className="w-full max-w-[95vw] mx-auto">
						{isMobile ? (
							<MobileArbitrageFilters
								filters={filters}
								setFilters={setFilters}
								setIsFiltersOpen={setIsFiltersOpen}
								email={user?.email}
								isMobile={isMobile}
								queries={queries}
								setQueries={setQueries}
								user={userState}
								setUser={setUserState}
							/>
						) : (
							<FiltersContainerArbitrage
								defaultStake={globalStake}
								filters={filters}
								setFilters={setFilters}
								setIsFiltersOpen={setIsFiltersOpen}
								email={user?.email}
								isMobile={isMobile}
								isArbitrage={true}
							/>
						)}
					</div>
				</div>
			)}
			{!isMobile && !isFree && !isApprentice && (
				<div className="px-4 md:px-4 xl:px-6 pt-8">
					<div className="w-full mx-auto" style={{ 
						overflowX: 'auto', 
						overflowY: 'visible',
						borderRadius: '16px',
						background: 'linear-gradient(135deg, #17191d 0%, #1a1d23 100%)',
						padding: '0',
						margin: '0',
						position: 'relative',
						zIndex: 1,
						boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
						border: '1px solid rgba(255, 255, 255, 0.1)',
						backdropFilter: 'blur(10px)',
						maxWidth: '100%',
						width: '100%'
					}}>
					<Table
						id="arbitrage-table"
						sx={{
							'& .MuiTableCell-root': {
								fontFamily: 'Mulish, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
								fontSize: '0.875rem',
								lineHeight: 1.5,
							},
							backgroundColor: 'transparent',
							tableLayout: 'fixed',
							width: '100%',
							'& .MuiTableContainer-root': {
								borderRadius: '16px',
							}
						}}>
						<TableHead
							sx={{
								'& .MuiTableRow-root:first-of-type .MuiTableCell-root:first-of-type': {
									borderTopLeftRadius: '16px',
								},
								'& .MuiTableRow-root:first-of-type .MuiTableCell-root:last-of-type': {
									borderTopRightRadius: '16px',
								},
								'& .MuiTableCell-root': {
									borderBottom: 'none',
									borderRight: 'none',
									borderLeft: 'none',
									background: 'transparent',
								}
							}}
							className="tableHead">
							<TableRow sx={{ 
								height: 80,
								'&:hover': {
									backgroundColor: 'rgba(255, 255, 255, 0.05)',
								}
							}}>
								<TableCell
									sx={{ width: '8%', textAlign: 'left' }}
									className="tableHeadCell">
									LEAGUE
								</TableCell>
								<TableCell
									sx={{ width: '8%', textAlign: 'left' }}
									className="tableHeadCell">
									MARKET
								</TableCell>
								<TableCell
									sx={{ width: '12%', textAlign: 'left' }}
									onClick={() => {
										if (sortColumn === 'time') {
											setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
										} else {
											setSortColumn('time');
											setSortDirection('asc');
										}
									}}
									className="tableHeadCell cursor-pointer">
									<span className="flex items-center">
										EVENT
										{sortColumn === 'time' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
									</span>
								</TableCell>
								<TableCell colSpan={2} sx={{ width: '40%' }} className="tableHeadCell">
									BOOKS
								</TableCell>
								<TableCell
									sx={{ width: '8%', textAlign: 'left' }}
									onClick={() => {
										if (sortColumn === 'roi') {
											setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
										} else {
											setSortColumn('roi');
											setSortDirection('desc');
										}
									}}
									className="tableHeadCell cursor-pointer">
									<span className="flex items-center">
										ROI
										{sortColumn === 'roi' && (sortDirection === 'asc' ? ' ▲' : ' ▼')}
									</span>
								</TableCell>
								<TableCell sx={{ width: '12%', textAlign: 'left' }} className="tableHeadCell">PROFIT</TableCell>
								<TableCell sx={{ width: '12%', textAlign: 'left' }} className="tableHeadCell">PLACED?</TableCell>
							</TableRow>
						</TableHead>
						{isLoading ? (
							// Show loading skeleton rows
							<TableBody className={`tableBody ${isLoading ? 'loading' : ''}`}>
								{Array.from({ length: 5 }).map((_, index) => (
									<TableRow key={`loading-${index}`} className="evtable-row">
										<TableCell style={{ color: '#f0f2f6', width: '8%' }}>
											<div style={{ 
												height: '20px', 
												backgroundColor: '#363844', 
												borderRadius: '4px',
												animation: 'pulse 1.5s ease-in-out infinite'
											}} />
										</TableCell>
										<TableCell style={{ color: '#f0f2f6', width: '8%' }}>
											<div style={{ 
												height: '20px', 
												backgroundColor: '#363844', 
												borderRadius: '4px',
												animation: 'pulse 1.5s ease-in-out infinite'
											}} />
										</TableCell>
										<TableCell style={{ color: '#f0f2f6', width: '12%' }}>
											<div style={{ 
												height: '20px', 
												backgroundColor: '#363844', 
												borderRadius: '4px',
												animation: 'pulse 1.5s ease-in-out infinite'
											}} />
										</TableCell>
										<TableCell style={{ color: '#f0f2f6', width: '20%' }}>
											<div style={{ 
												height: '20px', 
												backgroundColor: '#363844', 
												borderRadius: '4px',
												animation: 'pulse 1.5s ease-in-out infinite'
											}} />
										</TableCell>
										<TableCell style={{ color: '#f0f2f6', width: '20%' }}>
											<div style={{ 
												height: '20px', 
												backgroundColor: '#363844', 
												borderRadius: '4px',
												animation: 'pulse 1.5s ease-in-out infinite'
											}} />
										</TableCell>
										<TableCell style={{ color: '#f0f2f6', width: '8%' }}>
											<div style={{ 
												height: '20px', 
												backgroundColor: '#363844', 
												borderRadius: '4px',
												animation: 'pulse 1.5s ease-in-out infinite'
											}} />
										</TableCell>
										<TableCell style={{ color: '#f0f2f6', width: '12%' }}>
											<div style={{ 
												height: '20px', 
												backgroundColor: '#363844', 
												borderRadius: '4px',
												animation: 'pulse 1.5s ease-in-out infinite'
											}} />
										</TableCell>
										<TableCell style={{ color: '#f0f2f6', width: '12%' }}>
											<div style={{ 
												height: '20px', 
												backgroundColor: '#363844', 
												borderRadius: '4px',
												animation: 'pulse 1.5s ease-in-out infinite'
											}} />
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						) : (
							<TableBody className={`tableBody ${isLoading ? 'loading' : ''}`}>
								{Array.isArray(visibleRows) &&
									visibleRows.map((row, i) => {
										const splitoutcomeid = row.outcome_id_normalized_no_book.split(':');
										const splitoutcomeidbothbooks = row.outcome_id_normalized_both_books.split(':');
										const overBook = splitoutcomeidbothbooks[4];
										const underBook = splitoutcomeidbothbooks[5];
										const arbReturns = calculateArbReturn(row.bet_price_over, row.stakeA, row.bet_price_under, row.stakeB);

										// if (i === 0) {
										// 	console.log(row);
										// }
										let condition = row.game;
										let betNameOver = `${splitoutcomeid[2]} over ${splitoutcomeid[3]} ${splitoutcomeid[1]}`;
										let betNameUnder = `${splitoutcomeid[2]} under ${splitoutcomeid[3]} ${splitoutcomeid[1]}`;

										if (row.market === 'Moneyline') {
											betNameUnder = row.game.home_team_display;
											betNameOver = row.game.away_team_display;
										}

										// Estimate if text would wrap (rough heuristic: ~40 chars per line at this font size)
										const overLikelyWraps = betNameOver.length > 40;
										const underLikelyWraps = betNameUnder.length > 40;
										// When there's a mismatch (one wraps, other doesn't), both need same height
										const hasMismatch = (overLikelyWraps && !underLikelyWraps) || (!overLikelyWraps && underLikelyWraps);

										if (condition) {
											let backgroundColorClass = selectedRow === row.outcome_id_normalized_both_books ? 'evtable-row-selected' : 'evtable-row1';
											if (i % 2 !== 0) {
												backgroundColorClass = selectedRow === row.outcome_id_normalized_both_books ? 'evtable-row-selected' : 'evtable-row2';
											}

											return (
												<>
													<TableRow
														onClick={async () => {
															if (selectedRow === row.outcome_id_normalized_both_books) {
																setSelectedRow();
																setSelectedSport();
																setSelectedRowIndex(null);
															} else {
																if (row.outcome_id_normalized_both_books !== '') {
																	setSelectedRow(row.outcome_id_normalized_both_books);
																	setSelectedSport(row.sport);
																	setSelectedRowIndex(i); // 👈 Save the index
													const relatedOdds = await getRelatedOdds(
														row.outcome_id_normalized_no_book,
														row.sport,
														{ market: row.market, points: row.points, home_team: row.game?.home_team_display || row.home_team }
													);
																	setRelatedOdds(relatedOdds);
																} else {
																	// setIsUpgradeModalOpen(true);
																}
															}
														}}
														className={`${backgroundColorClass} ${selectedRow === row.outcome_id_normalized_both_books && 'selectedrow'} evtable-row`}
														style={{ 
															transition: 'all 0.3s ease-in-out',
															cursor: 'pointer'
														}}
														key={i}>
														<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow === row.outcome_id_normalized_both_books ? '#363844' : '', width: '8%', padding: '16px 8px' }}>
															{row.league}
														</TableCell>
														<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow === row.outcome_id_normalized_both_books ? '#363844' : '', width: '8%', padding: '16px 8px' }}>
															{splitoutcomeid[1]}
														</TableCell>
														<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow === row.outcome_id_normalized_both_books ? '#363844' : '', width: '12%', padding: '16px 8px' }}>
															<Typography color="gray" variant="body2" sx={{ mb: 0.5 }}>
																{new Date(row.game.start_date).toLocaleString()}
															</Typography>
															<Typography variant="caption">
																{row.game.away_team_display} @ {row.game.home_team_display}
															</Typography>
														</TableCell>
														{/* Books: Over */}
														<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow === row.outcome_id_normalized_both_books ? '#363844' : '', width: '20%', verticalAlign: 'top', padding: '16px 8px' }}>
															<Box sx={{ overflow: 'hidden', wordBreak: 'break-word', display: 'flex', flexDirection: 'column' }}>
																<Box sx={{ mb: 1.5, height: hasMismatch ? '45px' : 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
																	<Typography sx={{ lineHeight: 1.3, fontSize: '0.875rem', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{betNameOver}</Typography>
																</Box>
																<div className="flex flex-row" style={{ gap: '6px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
																	<div style={{ flexShrink: 0 }}>
																		<LogoGenerator sportsbook={overBook} />
																	</div>
																	<div style={{ flex: '1 1 auto', minWidth: 0 }}>
																		<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Odds: {addPlusSign(Math.round(row.bet_price_over))}</Typography>

																		<div className="flex flex-row" style={{ gap: '4px', alignItems: 'center' }}>
																			<div style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Stake</div>
																			<div onClick={(e) => e.stopPropagation()}>
																				<StakeInput
																					value={row.stakeA}
																					onChange={(val) => {
																						setArbResults((prev) =>
																							prev.map((row, index) => {
																								if (index === i) {
																										const updated = {
																											...row,
																											stakeA: Math.round(val),
																											stakeAUserEdited: true,
																										};
																									// if (forceOptimalStakes && !row.stakeBUserEdited) {
																									if (forceOptimalStakes) {
																										updated.stakeB = Math.round(calculateHedgeStake(row.bet_price_over, val, row.bet_price_under));
																									}
																									return updated;
																								}
																								return row;
																							})
																						);
																					}}
																				/>
																			</div>
																		</div>
																	</div>
																	{row.linkOver && <div style={{ flexShrink: 0 }}><BetLinkButton link={row.linkOver} /></div>}
																</div>
															</Box>
														</TableCell>
														{/* Books: Under */}
														<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow === row.outcome_id_normalized_both_books ? '#363844' : '', width: '20%', verticalAlign: 'top', padding: '16px 8px' }}>
															<Box sx={{ overflow: 'hidden', wordBreak: 'break-word', display: 'flex', flexDirection: 'column' }}>
																<Box sx={{ mb: 1.5, height: hasMismatch ? '45px' : 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start' }}>
																	<Typography sx={{ lineHeight: 1.3, fontSize: '0.875rem', wordBreak: 'break-word', overflowWrap: 'break-word' }}>{betNameUnder}</Typography>
																</Box>
																<div className="flex flex-row" style={{ gap: '6px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
																	<div style={{ flexShrink: 0 }}>
																		<LogoGenerator sportsbook={underBook} />
																	</div>
																	<div style={{ flex: '1 1 auto', minWidth: 0 }}>
																		<Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>Odds: {addPlusSign(Math.round(row.bet_price_under))}</Typography>

																		<div className="flex flex-row" style={{ gap: '4px', alignItems: 'center' }}>
																			<div style={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Stake</div>
																			<div onClick={(e) => e.stopPropagation()}>
																				<StakeInput
																					value={row.stakeB}
																					onChange={(val) => {
																						setArbResults((prev) =>
																							prev.map((row, index) => {
																								if (index === i) {
																										const updated = {
																											...row,
																											stakeB: Math.round(val),
																											stakeBUserEdited: true,
																										};
																									// if (forceOptimalStakes && !row.stakeAUserEdited) {
																									if (forceOptimalStakes) {
																										updated.stakeA = Math.round(calculateHedgeStake(row.bet_price_under, val, row.bet_price_over));
																									}
																									return updated;
																								}
																								return row;
																							})
																						);
																					}}
																				/>
																			</div>
																		</div>
																	</div>
																	{row.linkUnder && <div style={{ flexShrink: 0 }}><BetLinkButton link={row.linkUnder} /></div>}
																</div>
															</Box>
														</TableCell>
														<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow === row.outcome_id_normalized_both_books ? '#363844' : '', width: '8%', padding: '16px 8px' }}>
															<EVPercent ev={row.roi * 100 - 100} />
														</TableCell>
														<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow === row.outcome_id_normalized_both_books ? '#363844' : '', width: '12%', padding: '16px 8px' }}>
															<Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
																<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
																	<Typography variant="caption" sx={{ color: '#f0f2f6', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>If Over wins:</Typography>
																	<Typography variant="caption" sx={{ color: '#f0f2f6', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>${arbReturns.sideA_return.toFixed(2)}</Typography>
																</Box>
																<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
																	<Typography variant="caption" sx={{ color: '#f0f2f6', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>If Under wins:</Typography>
																	<Typography variant="caption" sx={{ color: '#f0f2f6', fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap' }}>${arbReturns.sideB_return.toFixed(2)}</Typography>
																</Box>
															</Box>
														</TableCell>
														<TableCell style={{ color: '#f0f2f6', backgroundColor: selectedRow === row.outcome_id_normalized_both_books ? '#363844' : '', width: '12%', padding: '16px 8px', verticalAlign: 'middle' }}>
															<Box sx={{ display: 'flex', flexDirection: 'row', gap: '8px', alignItems: 'center' }}>
																<Checkbox
																	onClick={(e) => {
																		e.stopPropagation();
																	}}
																	sx={{
																		color: selectedRow === row.outcome_id_normalized_both_books ? '#7a7e96' : '#363844', // Default border color when unchecked
																		'&.Mui-checked': {
																			color: '#28792a', // Border and check color when checked
																		},
																		'&:hover': {
																			color: '#5dcc60', // Border color on hover
																		},
																		padding: 0,
																		flexShrink: 0
																	}}
																	onChange={async (e) => {
																		if (row.outcome_id_normalized_both_books !== '') {
																			if (e.target.checked) {
																				// Mark as placed
																				const newBet = { id: row.outcome_id_normalized_both_books, time: new Date(), bet1Value: '', bet2Value: '' };
																				setPlacedBets([...placedBets, newBet]);
																				await setBetPlaced(row.outcome_id_normalized_both_books, user.email, JSON.stringify({ bet1: '', bet2: '' }));
																			} else {
																				const copy = JSON.parse(JSON.stringify(placedBets));
																				const newCopy = removeObjectWithProperty(copy, 'id', row.outcome_id_normalized_both_books);
																				setPlacedBets(newCopy);
																				await removeBetPlaced(row.outcome_id_normalized_both_books, user.email);
																			}
																		}
																	}}
																	checked={placedBets?.some((placedBet) => placedBet.id === row.outcome_id_normalized_both_books)}
																/>
																{placedBets?.some((placedBet) => placedBet.id === row.outcome_id_normalized_both_books) && (
																	<Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: 0 }}>
																		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
																			<Typography variant="caption" sx={{ fontSize: '10px', color: '#b1b6c3', lineHeight: 1 }}>Bet 1</Typography>
																			<Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
																				<span style={{ position: 'absolute', left: '4px', color: '#b1b6c3', fontSize: '11px', pointerEvents: 'none', zIndex: 1 }}>$</span>
																				<input
																					onClick={(e) => {
																						e.stopPropagation();
																					}}
																					type="text"
																					onChange={async (e) => {
																						// Remove all non-numeric characters except commas
																						let value = e.target.value.replace(/[^0-9,]/g, '');
																						// Remove commas for storage
																						const numericValue = value.replace(/,/g, '');
																						const copy = JSON.parse(JSON.stringify(placedBets));
																						let placedBet = copy.find((pb) => pb.id === row.outcome_id_normalized_both_books);
																						if (!placedBet) {
																							placedBet = { id: row.outcome_id_normalized_both_books, time: new Date(), bet1Value: '', bet2Value: '' };
																							copy.push(placedBet);
																						}
																						placedBet.bet1Value = numericValue;
																						setPlacedBets(copy);
																						const jsonValue = JSON.stringify({ bet1: numericValue, bet2: placedBet.bet2Value || '' });
																						await setBetPlaced(row.outcome_id_normalized_both_books, user.email, jsonValue);
																					}}
																					value={placedBets?.find((placedbet) => placedbet.id === row.outcome_id_normalized_both_books)?.bet1Value ? Number(placedBets.find((placedbet) => placedbet.id === row.outcome_id_normalized_both_books).bet1Value).toLocaleString() : ''}
																					style={{
																						width: '80px',
																						padding: '2px 4px 2px 14px',
																						border: '1px solid rgba(255, 255, 255, 0.2)',
																						outline: 'none',
																						backgroundColor: '#363844',
																						color: '#b1b6c3',
																						borderRadius: '3px',
																						fontSize: '11px',
																						boxSizing: 'border-box',
																						height: '24px',
																						lineHeight: '24px',
																					}}
																					placeholder="0"
																				/>
																			</Box>
																		</Box>
																		<Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
																			<Typography variant="caption" sx={{ fontSize: '10px', color: '#b1b6c3', lineHeight: 1 }}>Bet 2</Typography>
																			<Box sx={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
																				<span style={{ position: 'absolute', left: '4px', color: '#b1b6c3', fontSize: '11px', pointerEvents: 'none', zIndex: 1 }}>$</span>
																				<input
																					onClick={(e) => {
																						e.stopPropagation();
																					}}
																					type="text"
																					onChange={async (e) => {
																						// Remove all non-numeric characters except commas
																						let value = e.target.value.replace(/[^0-9,]/g, '');
																						// Remove commas for storage
																						const numericValue = value.replace(/,/g, '');
																						const copy = JSON.parse(JSON.stringify(placedBets));
																						let placedBet = copy.find((pb) => pb.id === row.outcome_id_normalized_both_books);
																						if (!placedBet) {
																							placedBet = { id: row.outcome_id_normalized_both_books, time: new Date(), bet1Value: '', bet2Value: '' };
																							copy.push(placedBet);
																						}
																						placedBet.bet2Value = numericValue;
																						setPlacedBets(copy);
																						const jsonValue = JSON.stringify({ bet1: placedBet.bet1Value || '', bet2: numericValue });
																						await setBetPlaced(row.outcome_id_normalized_both_books, user.email, jsonValue);
																					}}
																					value={placedBets?.find((placedbet) => placedbet.id === row.outcome_id_normalized_both_books)?.bet2Value ? Number(placedBets.find((placedbet) => placedbet.id === row.outcome_id_normalized_both_books).bet2Value).toLocaleString() : ''}
																					style={{
																						width: '80px',
																						padding: '2px 4px 2px 14px',
																						border: '1px solid rgba(255, 255, 255, 0.2)',
																						outline: 'none',
																						backgroundColor: '#363844',
																						color: '#b1b6c3',
																						borderRadius: '3px',
																						fontSize: '11px',
																						boxSizing: 'border-box',
																						height: '24px',
																						lineHeight: '24px',
																					}}
																					placeholder="0"
																				/>
																			</Box>
																		</Box>
																	</Box>
																)}
															</Box>
														</TableCell>
													</TableRow>
													{selectedRow === row.outcome_id_normalized_both_books && relatedOdds && (
														<TableRow className="dropdown evtable-dropdown">
															<TableCell style={{ padding: 0 }} colSpan={8}>
																<RelatedOddsDropdown
																	isArbitrage={true}
																	relatedOdds={relatedOdds}
																	isOver={row.ev_over > row.ev_under}
																	sportsbooksToHighlight={[splitoutcomeidbothbooks[4], splitoutcomeidbothbooks[5]]}
																	sportsbook_dumb={row.sportsbook_dumb}
																	outcome_id_normalized={selectedRow}
																	sharpBook={`${splitoutcomeidbothbooks[4]},${splitoutcomeidbothbooks[5]}`}
																	sharpFilter={''}
																	fairValue={null}
																	selectedRow={selectedRow}
																	selectedSport={selectedSport}
																	overSide={'bet_price_over'}
																	underSide={'bet_price_under'}
																	tutorial={false}
																	isLive={isLive}
																	sharpPriceOver={null}
																	sharpPriceUnder={null}
																/>
															</TableCell>
														</TableRow>
													)}
												</>
											);
										}
										return null;
									})}
							</TableBody>
						)}
					</Table>
					
					{/* Loading Overlay */}
					{isLoading && (
						<div style={{
							position: 'absolute',
							top: 0,
							left: 0,
							right: 0,
							bottom: 0,
							backgroundColor: 'rgba(23, 25, 29, 0.8)',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'center',
							zIndex: 10,
							borderRadius: '16px'
						}}>
							<img 
								style={{ 
									width: '300px',
									marginBottom: '20px'
								}} 
								src={wwlogo_gif} 
								alt="loading icon" 
							/>
						</div>
					)}
					</div>
				</div>
			)}
			{isMobile && isLoading && !isFree && !isApprentice && (
				<div style={{
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'center',
					alignItems: 'center',
					padding: '40px 20px',
					minHeight: '400px'
				}}>
					<img 
						style={{ 
							width: '200px',
							marginBottom: '20px'
						}} 
						src={wwlogo_gif} 
						alt="loading icon" 
					/>
				</div>
			)}
			{isMobile && !isLoading && !isFree && !isApprentice && (
				<>
					{/* Add a small space before the first card */}
					<Box sx={{ height: 32 }} />
					{visibleRows.map((row, i) => {
						const splitoutcomeid = row.outcome_id_normalized_no_book.split(':');
						const splitoutcomeidbothbooks = row.outcome_id_normalized_both_books.split(':');
						const overBook = splitoutcomeidbothbooks[4];
						const underBook = splitoutcomeidbothbooks[5];
						const arbReturns = calculateArbReturn(row.bet_price_over, row.stakeA, row.bet_price_under, row.stakeB);

						let condition = row.game;
						let betNameOver = `${splitoutcomeid[2]} over ${splitoutcomeid[3]} ${splitoutcomeid[1]}`;
						let betNameUnder = `${splitoutcomeid[2]} under ${splitoutcomeid[3]} ${splitoutcomeid[1]}`;

						// Construct play title
						let playTitle = '';
						if (row.market === 'Moneyline') {
							playTitle = `${row.game.away_team_display} vs ${row.game.home_team_display}`;
						} else {
							playTitle = `${splitoutcomeid[2]} ${splitoutcomeid[3]} ${splitoutcomeid[1]}`;
						}

						if (row.market === 'Moneyline') {
							betNameUnder = row.game.home_team_display;
							betNameOver = row.game.away_team_display;
						}
						if (condition) {
							return (
								<Card
									key={i}
									sx={{
										backgroundColor: '#1a1d23',
										border: '1px solid #2d3748',
										borderRadius: '12px',
										boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
										mb: 3,
										mx: 'auto',
										width: '97%',
										maxWidth: 430,
										position: 'relative',
										overflow: 'hidden',
									}}>
									
									{/* Enhanced Game Info Section - Now on Top */}
									<Box sx={{ p: 2, borderBottom: '1px solid #374151' }}>
										<Box sx={{ mb: 1.5 }}>
											<Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 12, display: 'block', mb: 0.5 }}>
												{new Date(row.game.start_date).toLocaleString()}
											</Typography>
											<Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: 18, mb: 0.5 }}>
												{row.game.away_team_display} @ {row.game.home_team_display}
											</Typography>
											<Typography variant="body1" sx={{ color: '#e5e7eb', fontSize: 16, fontWeight: 500 }}>
												{row.league} • {playTitle}
											</Typography>
										</Box>
									</Box>

									{/* Prominent ROI and Profit Section - Now Below Game Info */}
									<Box sx={{ 
										p: 2, 
										backgroundColor: arbReturns.min_return > 0 ? 'rgba(19, 172, 89, 0.1)' : 'rgba(196, 25, 28, 0.1)',
										borderBottom: '1px solid #374151'
									}}>
										<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
											<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
												<Typography variant="caption" sx={{ color: '#e5e7eb', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.8px', mb: 0.5, fontWeight: 600 }}>
													ROI
												</Typography>
												<Box sx={{
													backgroundColor: arbReturns.min_return > 0 ? '#13ac59' : '#C4191C',
													color: 'white',
													px: 2,
													py: 1,
													borderRadius: 2,
													minWidth: 80,
													textAlign: 'center'
												}}>
													<Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>
														{((row.roi * 100 - 100)).toFixed(2)}%
													</Typography>
												</Box>
											</Box>
											<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
												<Typography variant="caption" sx={{ color: '#e5e7eb', fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.8px', mb: 0.5, fontWeight: 600 }}>
													Profit
												</Typography>
												<Box sx={{
													backgroundColor: arbReturns.min_return > 0 ? '#13ac59' : '#C4191C',
													color: 'white',
													px: 2,
													py: 1,
													borderRadius: 2,
													minWidth: 80,
													textAlign: 'center'
												}}>
													<Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18 }}>
														${arbReturns.min_return >= 1000 ? Math.round(arbReturns.min_return).toLocaleString() : arbReturns.min_return.toFixed(2)}
													</Typography>
												</Box>
											</Box>
										</Box>
									</Box>

									{/* Main Bet Section - Over */}
									<Box sx={{ p: 2, backgroundColor: 'rgba(31, 41, 55, 0.5)' }}>
										<Box sx={{ mb: 2 }}>
											<Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: 16 }}>
												{betNameOver}
											</Typography>
										</Box>
										
										{/* Betting Info Row */}
										<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
												<LogoGenerator sportsbook={overBook} />
												<Box sx={{
													backgroundColor: 'rgba(255, 255, 255, 0.1)',
													border: '1px solid rgba(255, 255, 255, 0.2)',
													borderRadius: 2,
													px: 2,
													py: 1,
													minWidth: 60,
													textAlign: 'center'
												}}>
													<Typography sx={{ color: 'white', fontWeight: 700, fontSize: 18 }}>
														{addPlusSign(Math.round(row.bet_price_over))}
													</Typography>
												</Box>
												{row.linkOver && <BetLinkButton link={row.linkOver} mobile={true} />}
											</Box>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<Checkbox
													onClick={(e) => e.stopPropagation()}
													sx={{
														color: placedBets?.some((placedBet) => placedBet.id === `${row.outcome_id_normalized_both_books}_over`) ? '#28792a' : '#363844',
														'&.Mui-checked': { color: '#28792a' },
														'&:hover': { color: '#5dcc60' },
														transform: 'scale(1.2)',
													}}
													onChange={async (e) => {
														const overBetId = `${row.outcome_id_normalized_both_books}_over`;
														if (overBetId !== '') {
															if (e.target.checked) {
																await setBetPlaced(overBetId, user.email);
																setPlacedBets([...placedBets, { id: overBetId, time: new Date() }]);
															} else {
																const copy = JSON.parse(JSON.stringify(placedBets));
																const newCopy = removeObjectWithProperty(copy, 'id', overBetId);
																setPlacedBets(newCopy);
																await removeBetPlaced(overBetId, user.email);
															}
														}
													}}
													checked={placedBets?.some((placedBet) => placedBet.id === `${row.outcome_id_normalized_both_books}_over`)}
												/>
												<Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 12 }}>Placed</Typography>
											</Box>
										</Box>
										<Box sx={{ mt: 2 }}>
											<Typography variant="body1" sx={{ color: '#9ca3af', fontSize: 16, fontWeight: 500 }}>
												Stake: <span style={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>${Math.round(Number(row.stakeA)).toLocaleString()}</span>
											</Typography>
										</Box>
									</Box>

									{/* Under Bet Section */}
									<Box sx={{ p: 2, backgroundColor: 'rgba(17, 24, 39, 0.3)' }}>
										<Box sx={{ mb: 2 }}>
											<Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: 16 }}>
												{betNameUnder}
											</Typography>
										</Box>
										
										{/* Betting Info Row */}
										<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
												<LogoGenerator sportsbook={underBook} />
												<Box sx={{
													backgroundColor: 'rgba(255, 255, 255, 0.1)',
													border: '1px solid rgba(255, 255, 255, 0.2)',
													borderRadius: 2,
													px: 2,
													py: 1,
													minWidth: 60,
													textAlign: 'center'
												}}>
													<Typography sx={{ color: 'white', fontWeight: 700, fontSize: 18 }}>
														{addPlusSign(Math.round(row.bet_price_under))}
													</Typography>
												</Box>
												{row.linkUnder && <BetLinkButton link={row.linkUnder} mobile={true} />}
											</Box>
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<Checkbox
													onClick={(e) => e.stopPropagation()}
													sx={{
														color: placedBets?.some((placedBet) => placedBet.id === `${row.outcome_id_normalized_both_books}_under`) ? '#28792a' : '#363844',
														'&.Mui-checked': { color: '#28792a' },
														'&:hover': { color: '#5dcc60' },
														transform: 'scale(1.2)',
													}}
													onChange={async (e) => {
														const underBetId = `${row.outcome_id_normalized_both_books}_under`;
														if (underBetId !== '') {
															if (e.target.checked) {
																await setBetPlaced(underBetId, user.email);
																setPlacedBets([...placedBets, { id: underBetId, time: new Date() }]);
															} else {
																const copy = JSON.parse(JSON.stringify(placedBets));
																const newCopy = removeObjectWithProperty(copy, 'id', underBetId);
																setPlacedBets(newCopy);
																await removeBetPlaced(underBetId, user.email);
															}
														}
													}}
													checked={placedBets?.some((placedBet) => placedBet.id === `${row.outcome_id_normalized_both_books}_under`)}
												/>
												<Typography variant="caption" sx={{ color: '#9ca3af', fontSize: 12 }}>Placed</Typography>
											</Box>
										</Box>
										<Box sx={{ mt: 2 }}>
											<Typography variant="body1" sx={{ color: '#9ca3af', fontSize: 16, fontWeight: 500 }}>
												Stake: <span style={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>${Math.round(Number(row.stakeB)).toLocaleString()}</span>
											</Typography>
										</Box>
									</Box>

									{/* Actions Section */}
									<Box sx={{ px: 2, py: 1.5, backgroundColor: 'rgba(31, 41, 55, 0.5)', borderTop: '1px solid rgba(75, 85, 99, 0.3)' }}>
										<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
											<Button
												onClick={async () => {
													if (selectedRow === row.outcome_id_normalized_both_books) {
														setSelectedRow();
														setSelectedSport();
														setSelectedRowIndex(null);
													} else {
														if (row !== '') {
															setSelectedRow(row.outcome_id_normalized_both_books);
															setSelectedSport(row.sport);
															setSelectedRowIndex(i);
														const relatedOdds = await getRelatedOdds(
															row.outcome_id_normalized_no_book,
															row.sport,
															{ market: row.market, points: row.points, home_team: row.game?.home_team_display || row.home_team }
														);
															setRelatedOdds(relatedOdds);
														} else {
															setIsUpgradeModalOpen(true);
														}
													}
												}}
												sx={{
													backgroundColor: '#2563eb',
													color: 'white',
													fontWeight: 600,
													py: 1,
													px: 4,
													borderRadius: 2,
													fontSize: 14,
													textTransform: 'none',
													minWidth: 140,
													'&:hover': {
														backgroundColor: '#1d4ed8',
													},
												}}>
												{selectedRow === row.outcome_id_normalized_both_books ? 'Hide Odds' : 'Compare Odds'}
											</Button>
										</Box>
									</Box>

									{/* Related Odds Dropdown */}
									{selectedRow === row.outcome_id_normalized_both_books && relatedOdds && (
										<RelatedOddsDropdown
											isArbitrage={true}
											relatedOdds={relatedOdds}
											isOver={row.ev_over > row.ev_under}
											sportsbooksToHighlight={[splitoutcomeidbothbooks[4], splitoutcomeidbothbooks[5]]}
											sportsbook_dumb={row.sportsbook_dumb}
											outcome_id_normalized={selectedRow}
											sharpBook={`${splitoutcomeidbothbooks[4]},${splitoutcomeidbothbooks[5]}`}
											sharpFilter={''}
											isMobile={true}
											selectedRow={selectedRow}
											selectedSport={selectedSport}
											overSide={'bet_price_over'}
											underSide={'bet_price_under'}
											tutorial={false}
											isLive={isLive}
											sharpPriceOver={null}
											sharpPriceUnder={null}
										/>
									)}
								</Card>
							);
						} else return <></>;
					})}
				</>
			)}
			{<UpgradeModal message={'Arbitrage page not available for your plan'} isOpen={isUpgradeModalOpen} setIsOpen={() => setIsUpgradeModalOpen(false)} />}
		</div>
	);
};
